import { useQuery } from '@apollo/client/react';

import { FeatureFlags } from '@constants/featureFlags';
import { gql } from '@generated/gql/gql';
import { BundleQuoteFragment } from '@generated/gql/graphql';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { isBroadbandQuote } from '@utils/typeCheck';

gql(`
  fragment Assistant on AttributionDetails {
    assistant {
      partner {
        id
      }
      sales {
        oktaUsername
      }
    }
  }
`);

const AssistantQuery = gql(`
  query Assistant($basketId: ID!) {
    attributionDetails(basketId: $basketId) {
      ...Assistant
    }
  }
`);

enum AssistantType {
  PARTNER = 'partner',
  SALES = 'sales',
  UNASSISTED = 'unassisted',
}

export const useIsBroadbandInstallationEnabled = (
  selectedQuotes: BundleQuoteFragment[]
) => {
  const { basketId } = useBasketContext();

  const broadbandQuote = selectedQuotes.find(isBroadbandQuote);
  const broadbandTariff = broadbandQuote?.product.name ?? '';
  const isBroadbandInstallationRequired =
    broadbandQuote?.requirements.some(
      (r) => r.requirement.lineCharacteristics?.appointmentRequired
    ) ?? false;

  const { data } = useQuery(AssistantQuery, {
    variables: {
      basketId,
    },
    skip: !isBroadbandInstallationRequired,
    fetchPolicy: 'cache-first',
  });

  const assistantType: AssistantType | null = !data
    ? null
    : data?.attributionDetails?.assistant?.partner
      ? AssistantType.PARTNER
      : data?.attributionDetails?.assistant?.sales
        ? AssistantType.SALES
        : AssistantType.UNASSISTED;

  const { value: isBroadbandInstallationEnabled } = useFeatureFlag(
    FeatureFlags.BROADBAND_APPOINTMENT_BOOKING,
    false,
    {
      identifier: basketId,
      custom: {
        broadbandTariff,
        ...(assistantType && { assistantType }),
      },
    }
  );

  return isBroadbandInstallationRequired && isBroadbandInstallationEnabled;
};
